import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Table, Button, Modal, Spinner } from 'react-bootstrap';
import './adhoc-reports.scss';
import DateSelectorModal from './components/modals/modal-date-selector';
import ModalTaxAdjustmentDetails from './components/modals/modal-tax-adjustment-details';
import ReportsTable from './components/ad-hoc-reports-table/reports-table';
import reportTitleData from './components/reportTitleData/reportTitleData.json';
import ReportsService from '../../../../../../../../services/reports-service';
import { IdentityContext } from '../../../../../../../../context/identity-context';
import * as XLSX from 'xlsx';
import { ExcelPreviewModal } from './components/modals/excel-preview-modal';

const MAX_PREVIEW_ROWS = 500; // limit rows for performance

// Success modal component
const SuccessModal = ({ show, onHide }) => {
  useEffect(() => {
    if (show) {
      const timer = setTimeout(() => {
        window.location.reload();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Success</Modal.Title>
      </Modal.Header>
      <Modal.Body>Reports requested successfully!</Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => window.location.reload()}>
          Continue
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

// Failure modal component
const FailureModal = ({ show, onHide, errorMessage }) => (
  <Modal show={show} onHide={onHide} centered>
    <Modal.Header closeButton>
      <Modal.Title>Error</Modal.Title>
    </Modal.Header>
    <Modal.Body>{errorMessage || 'An error occurred. Please try again.'}</Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

// Download confirmation modal component
const DownloadConfirmationModal = ({ show, onCancel, onConfirm, loading }) => (
  <Modal show={show} onHide={onCancel} centered>
    <Modal.Header closeButton>
      <Modal.Title>Authorized Access Confirmation</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      <p>
        By proceeding to download this report, you confirm that you are an authorized user with permission to access this sensitive information.
      </p>
      <p>
        Once downloaded, the report will no longer be available for viewing on this platform. Do you wish to continue?
      </p>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="primary" onClick={onConfirm} disabled={loading}>
        {loading ? 'Downloading...' : 'Continue'}
      </Button>
    </Modal.Footer>
  </Modal>
);

const AdHocReports = ({ reports, securityName, isin, issuerName }) => {
  const [selectedReports, setSelectedReports] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [failureModalVisible, setFailureModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [reportData, setReportData] = useState(reports);
  const [currentReport, setCurrentReport] = useState(reportTitleData[0]);
  const [currentReportTitle, setCurrentReportTitle] = useState(reportTitleData[0].title);
  const [paymentDates, setPaymentDates] = useState([]);
  const [isFetchingDates, setIsFetchingDates] = useState(false);

  // States for download confirmation
  const [downloadReport, setDownloadReport] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // States for Excel Preview
  const [excelPreviewData, setExcelPreviewData] = useState([]);
  const [showExcelPreviewModal, setShowExcelPreviewModal] = useState(false);

  const reportService = new ReportsService();
  const { user } = useContext(IdentityContext);

  // Determine if the current report is one of the special ones
  const isSpecialReport =
    currentReportTitle.trim() === "BIR 2306 Request Letter" ||
    currentReportTitle.startsWith("Closure of Registry Service / Payment Account") ||
    currentReportTitle.startsWith("Consent Solicitation") ||
    currentReportTitle.startsWith("Credit Letter");

  // Toggle report selection
  const handleCheckboxChange = (report) => {
    setSelectedReports((prev) =>
      prev.some((selected) => selected.id === report.id)
        ? prev.filter((selected) => selected.id !== report.id)
        : [...prev, report]
    );
  };

  // Open modal function for Tax Adjustment Letter
  const handleOpenModal = async () => {
    if (currentReportTitle === "Tax Adjustment Letter") {
      setIsFetchingDates(true);
      try {
        const response = await reportService.issuerGetPaymentDates(isin);
        setPaymentDates(response.paymentDates);
      } catch (error) {
        console.error("Error fetching payment dates:", error);
      } finally {
        setIsFetchingDates(false);
      }
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => setModalVisible(false);

  const handleRequestReports = async () => {
    try {
      await reportService.issuerRequestAdhocReport(selectedReports);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error('Error requesting reports:', error);
      let errMsg = 'Failed to request reports.';
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'string') {
          try {
            const parsedData = JSON.parse(responseData);
            if (Array.isArray(parsedData) && parsedData.length > 0) {
              errMsg = parsedData[0].message || errMsg;
            } else if (parsedData.message) {
              errMsg = parsedData.message;
            }
          } catch {
            errMsg = responseData;
          }
        } else if (typeof responseData === 'object') {
          if (Array.isArray(responseData) && responseData.length > 0) {
            errMsg = responseData[0].message || errMsg;
          } else if (responseData.message) {
            errMsg = responseData.message;
          }
        }
      }
      setErrorMessage(errMsg);
      setFailureModalVisible(true);
    }
  };

  // For report types other than Tax Adjustment Letter
  const addReportsWithDates = (dates) => {
    const validDates = dates.filter((date) => date && date.trim());
    const existingDates = reportData
      .filter((report) => report.reportTitle === currentReportTitle)
      .map((report) => report.issuerRequestDate);
    const uniqueDates = validDates.filter((date) => !existingDates.includes(date));
    const newReports = uniqueDates.map((date, index) => ({
      id: `report-${Date.now()}-${index}`,
      isin,
      issuerName,
      issuerRequestDate: date,
      reportTitle: currentReportTitle,
      securityName: securityName || 'Default Security Name',
      status: '-',
      remarks: '-',
    }));
    setReportData((prevReports) => [...prevReports, ...newReports]);
  };

  // For Tax Adjustment Letter: add new report with tax adjustment details including the payment date
  const addTaxAdjustmentReport = (details) => {
    const newReport = {
      id: `tax-adjustment-${Date.now()}`,
      bpId: details.bpId,
      taxAdjustmentType: details.taxAdjustmentType,
      amount: details.amount,
      reportTitle: currentReportTitle,
      status: '-',
      isin,
      issuerName,
      issuerRequestDate: new Date().toISOString().split('T')[0],
      paymentDate: details.paymentDate,
      securityName,
    };
    setReportData((prevReports) => [...prevReports, newReport]);
  };

  // Helper to detect Excel by extension
  const isExcelFile = (url) => {
    try {
      const strippedUrl = url.split('?')[0].toLowerCase();
      return strippedUrl.endsWith('.xlsx') || strippedUrl.endsWith('.xls');
    } catch {
      return false;
    }
  };

  // Preview handler with normalization/truncation logic
  const handlePreview = async (report) => {
    try {
      let response;
      if (report.reportTitle === "Tax Adjustment Letter") {
        const reportType = report.reportType;
        const reportTitle = report.reportTitle;
        const date = report.paymentDate || report.issuerRequestDate || '';
        const options = { bpId: report.bpId, taxAdjustmentType: report.taxAdjustmentType };
        response = await reportService.retrieveIssuerAdhocReportFile(reportType, isin, reportTitle, date, options);
      } else if (
        report.reportTitle.startsWith("Closure of Registry Service") ||
        report.reportTitle.startsWith("Consent Solicitation") ||
        report.reportTitle.startsWith("Credit Letter")
      ) {
        const s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.paymentDate}/`;
        response = await reportService.retrieveEventBasedReport(s3Prefix);
      } else if (report.reportTitle === "BIR 2306 Request Letter") {
        const s3Prefix = `Ad-hoc Reports/${report.isin}/${report.reportTitle}/${report.bpId}/${report.holder}/${report.dateFrom || ''}${report.dateTo || ''}/`;
        response = await reportService.retrieveEventBasedReport(s3Prefix);
      } else {
        response = await reportService.retrieveReport(
          "Ad-hoc Reports",
          isin,
          report.reportTitle || currentReportTitle,
          report.issuerRequestDate || ''
        );
      }

      const fileURL = response.fileURL;
      if (!fileURL) throw new Error('No file URL returned');
      const urlObj = new URL(fileURL);
      const pathLower = urlObj.pathname.toLowerCase();

      // If PDF, open directly
      if (pathLower.endsWith('.pdf') || (response.contentType && response.contentType.includes('pdf'))) {
        const newWindow = window.open(fileURL, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.opener = null;
        return;
      }
      console.log(isExcelFile(fileURL));
      // If Excel, fetch & parse
      if (isExcelFile(fileURL)) {
        const fileRes = await fetch(fileURL);
        if (!fileRes.ok) throw new Error(`Fetch failed (${fileRes.status})`);
        const blob = await fileRes.blob();
        const arrayBuffer = await blob.arrayBuffer();
        let workbook;
        try {
          workbook = XLSX.read(arrayBuffer, { type: 'array' });
        } catch (parseErr) {
          console.error('Excel parse error:', parseErr);
          // fallback: open in new tab
          const newWindow = window.open(fileURL, '_blank', 'noopener,noreferrer');
          if (newWindow) newWindow.opener = null;
          return;
        }
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw rows
        const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        // truncate if needed
        const usedRows = rawRows.length > MAX_PREVIEW_ROWS
          ? rawRows.slice(0, MAX_PREVIEW_ROWS)
          : rawRows;
        // compute max columns
        const maxCols = usedRows.reduce((m, row) => Math.max(m, row.length), 0);
        // normalize rows to uniform length
        const spreadsheetData = usedRows.map(row =>
          Array.from({ length: maxCols }, (_, i) => ({
            value: row[i] !== undefined ? row[i] : '',
            readOnly: true
          }))
        );
        setExcelPreviewData(spreadsheetData);
        setShowExcelPreviewModal(true);
        return;
      }

      // Other file types: open directly
      const newWindow = window.open(fileURL, '_blank', 'noopener,noreferrer');
      if (newWindow) newWindow.opener = null;
    } catch (error) {
      console.error('Error fetching report preview:', error);
      alert('Failed to load preview: ' + (error.message || error));
    }
  };

  // Download confirmation handler
  const handleConfirmDownload = async () => {
    setDownloadLoading(true);
    try {
      let response;
      const report = downloadReport;
      if (!report) throw new Error('No report selected');
      if (report.reportTitle === "Tax Adjustment Letter") {
        const reportType = report.reportType;
        const reportTitle = report.reportTitle;
        const paymentDate = report.paymentDate;
        const options = { bpId: report.bpId, taxAdjustmentType: report.taxAdjustmentType };
        response = await reportService.retrieveReportIssuer(reportType, isin, reportTitle, paymentDate, user.attributes['custom:display_name'], options);
      } else if (report.reportTitle === "BIR 2306 Request Letter") {
        const reportType = report.reportType;
        const reportTitle = report.reportTitle;
        const paymentDate = report.paymentDate;
        const bir = { birBpId: report.bpId, holder: report.holder, dateFrom: report.dateFrom, dateTo: report.dateTo };
        response = await reportService.retrieveReportIssuer(reportType, isin, reportTitle, paymentDate, user.attributes['custom:display_name'], {}, bir);
      } else {
        const reportType = report.reportType;
        const reportTitle = report.reportTitle;
        const paymentDate = report.paymentDate || report.issuerRequestDate || '';
        response = await reportService.retrieveReportIssuer(reportType, isin, reportTitle, paymentDate, user.attributes['custom:display_name']);
      }

      const fileResponse = await fetch(response.fileURL);
      if (!fileResponse.ok) throw new Error('Network response was not ok');
      const blob = await fileResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const fileName = response.fileName || 'download.pdf';
      link.href = objectUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Failed to download: ' + (error.message || error));
    } finally {
      setShowDownloadModal(false);
      setDownloadLoading(false);
      window.location.reload();
    }
  };

  // New download confirmation handler
  const handleDownloadConfirmation = (report) => {
    setDownloadReport(report);
    setShowDownloadModal(true);
  };

  // Update report data when the selected report type changes
  useEffect(() => {
    if (currentReportTitle === "Tax Adjustment Letter") {
      const taxReports = reports.filter(
        (report) => report.reportTitle.trim() === currentReportTitle.trim()
      );
      setReportData(taxReports);
    } else if (currentReportTitle.startsWith("Consent Solicitation")) {
      const filteredReports = reports.filter(
        (report) => report.reportTitle.trim().startsWith("Consent Solicitation")
      );
      setReportData(filteredReports);
    } else {
      const filteredReports = reports.filter(
        (report) => report.reportTitle.trim() === currentReportTitle.trim()
      );
      setReportData(filteredReports);
    }
    setSelectedReports([]);
  }, [currentReportTitle, reports]);

  return (
    <div className="adhoc-reports">
      {/* Report Titles and Dropdown */}
      <Table bordered hover className="mb-3">
        <tbody>
          <tr>
            <td>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <select
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      const selectedReport = reportTitleData.find((r) => r.id === selectedId);
                      setCurrentReport(selectedReport);
                      setCurrentReportTitle(selectedReport?.title || '');
                    }}
                    value={currentReport.id}
                  >
                    {reportTitleData.map((report) => (
                      <option key={report.id} value={report.id}>
                        {report.title}
                      </option>
                    ))}
                  </select>
                </div>
                {!isSpecialReport && (
                  <Button
                    variant="secondary"
                    onClick={handleOpenModal}
                    disabled={isFetchingDates}
                  >
                    {currentReportTitle === "Tax Adjustment Letter"
                      ? (isFetchingDates ? <Spinner animation="border" size="sm" /> : "Enter Details")
                      : "Select Date"}
                  </Button>
                )}
              </div>
            </td>
          </tr>
        </tbody>
      </Table>

      {/* Main Reports Table */}
      <ReportsTable
        reportData={reportData}
        currentReportTitle={currentReportTitle}
        selectedReports={selectedReports}
        onCheckboxChange={handleCheckboxChange}
        onPreview={handlePreview}
        onDownload={handleDownloadConfirmation}
      />

      {/* Request Reports Button */}
      {!isSpecialReport && (
        <Button
          variant="primary"
          onClick={handleRequestReports}
          disabled={selectedReports.length === 0}
        >
          Request Reports
        </Button>
      )}

      {/* Conditionally render the appropriate modal */}
      {currentReportTitle === "Tax Adjustment Letter" ? (
        <ModalTaxAdjustmentDetails
          show={modalVisible}
          onClose={handleCloseModal}
          paymentDates={paymentDates}
          existingTaxAdjustments={
            reportData
              .filter(report => report.reportTitle === "Tax Adjustment Letter")
              .map(report => ({ bpId: report.bpId, taxAdjustmentType: report.taxAdjustmentType }))
          }
          onConfirm={(details) => {
            addTaxAdjustmentReport(details);
            setModalVisible(false);
          }}
        />
      ) : (
        <DateSelectorModal
          show={modalVisible}
          onClose={handleCloseModal}
          onConfirm={(dates) => {
            addReportsWithDates(dates);
            setModalVisible(false);
          }}
          onDateChange={(dates) => console.log('Current Dates:', dates)}
        />
      )}

      <SuccessModal
        show={successModalVisible}
        onHide={() => setSuccessModalVisible(false)}
      />
      <FailureModal
        show={failureModalVisible}
        onHide={() => setFailureModalVisible(false)}
        errorMessage={errorMessage}
      />

      {/* Download confirmation modal */}
      <DownloadConfirmationModal
        show={showDownloadModal}
        onCancel={() => setShowDownloadModal(false)}
        onConfirm={handleConfirmDownload}
        loading={downloadLoading}
      />

      {/* Excel Preview Modal */}
      {showExcelPreviewModal && (
        <ExcelPreviewModal
          show={showExcelPreviewModal}
          data={excelPreviewData}
          onClose={() => {
            setShowExcelPreviewModal(false);
            setExcelPreviewData([]);
          }}
        />
      )}
    </div>
  );
};

export default AdHocReports;
